import React, { useMemo, useRef, useState } from "react";
import { MapPin, Search, X, Zap, LogIn, LogOut, LocateFixed, Navigation, Satellite } from "lucide-react";
import Canvas from "./Canvas";
import { Input } from "./ui/input";
import { neighbourBBox } from "../lib/helpers";

export default function MobileView({
  bg, ppm, categories, elements, selected, selectedId, onSelect,
  isTeam = false, isAdmin = false, isSuperAdmin = false, isAuthenticated = false, onLogin, onLogout,
  geo, showUserLoc = false, toggleUserLoc, userDot = null, geoCalibrated = false,
  geoCalibDraft = [], geoPendingPixel = null, geoCapturing = false,
  geoDraftQuality = null,
  startGeoCalib, onGeoCalibPoint, captureGeoPoint, saveGeoCalibration, cancelGeoCalib,
  tool = "select",
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focusRequest, setFocusRequest] = useState(null);
  const inputRef = useRef(null);

  const cat = selected ? categories.find((c) => c.id === selected.category_id) : null;

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return elements.filter((el) => el.name.toLowerCase().includes(q)).slice(0, 12);
  }, [query, elements]);

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const sheetRef = useRef(null);

  const focusOnElement = (el) => {
    // zoom onto the element and its neighbours, leaving room for the details
    // sheet (team/admin) at the bottom of the screen
    setTimeout(() => {
      const inset = isTeam && sheetRef.current ? sheetRef.current.offsetHeight : 0;
      const bbox = neighbourBBox(elements, el.id, ppm);
      setFocusRequest({ bbox, ts: Date.now(), bottomInset: inset });
    }, 80);
  };

  // direct tap on the map: select + zoom onto the cluster
  const handleSelect = (id) => {
    onSelect(id);
    if (id) {
      const el = elements.find((e) => e.id === id);
      if (el) focusOnElement(el);
    }
  };

  const pickElement = (el) => {
    onSelect(el.id);
    setSearchOpen(false);
    setQuery("");
    focusOnElement(el);
  };

  return (
    <div className="h-[100dvh] w-screen overflow-hidden flex flex-col bg-zinc-50">
      <header className="relative h-12 shrink-0 flex items-center justify-between px-3 border-b border-black/30 bg-[#14161A] z-20">
        <div className="flex items-center gap-2 min-w-0 z-10">
          <MapPin size={18} className="text-[#8BC53F] shrink-0" strokeWidth={1.5} />
          <h1 data-testid="brand-title" className="font-head text-sm tracking-tight font-bold text-white truncate">BLF</h1>
        </div>
        <img src="/logo360.png" alt="360 Revolution" data-testid="mobile-header-logo"
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-7 pointer-events-none select-none" />
        <div className="flex items-center gap-2 z-10">
          <button data-testid="mobile-search-btn" onClick={openSearch}
            className="flex items-center gap-1 px-3 h-8 text-sm border bg-[#5E8A1E] text-white border-[#5E8A1E]">
            <Search size={15} strokeWidth={1.5} /> Caută
          </button>
          {isAuthenticated ? (
            <button data-testid="mobile-logout-btn" onClick={onLogout}
              className="flex items-center gap-1 px-3 h-8 text-sm border bg-white text-zinc-700 border-[#A8C97D]">
              <LogOut size={15} strokeWidth={1.5} /> Ieșire
            </button>
          ) : (
            <button data-testid="mobile-login-btn" onClick={onLogin}
              className="flex items-center gap-1 px-3 h-8 text-sm border bg-white text-[#5E8A1E] border-[#A8C97D]">
              <LogIn size={15} strokeWidth={1.5} /> Login
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 relative">
        <Canvas
          backgroundUrl={bg} ppm={ppm} categories={categories} elements={elements}
          mode="view" tool={tool === "geocalib" ? "geocalib" : "select"} selectedId={selectedId} onSelect={handleSelect}
          onPlace={() => {}} onElementDrag={() => {}} onElementCommit={() => {}}
          calibPoints={[]} onCalibPoint={() => {}} onRubberBand={() => {}}
          highlightIds={selectedId ? [selectedId] : []} viewDrag="pan"
          showLabelsOnly={false} showConsumption={false} focusRequest={focusRequest}
          canSeeDetails={isTeam}
          userDot={userDot}
          geoCalibPoints={tool === "geocalib" ? geoCalibDraft : null}
          onGeoCalibPoint={onGeoCalibPoint}
        />

        {/* location controls (all tiers) */}
        {tool !== "geocalib" && (
          <div className="absolute left-4 z-20 flex flex-col gap-1" style={{ bottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
            <button data-testid="mobile-toggle-user-loc-btn" onClick={toggleUserLoc} disabled={!geoCalibrated}
              className={`flex items-center gap-2 px-3 h-10 border shadow disabled:opacity-40 ${
                showUserLoc ? "bg-[#2563EB] text-white border-[#2563EB]" : "bg-white text-zinc-700 border-[#A8C97D]"
              }`}>
              <LocateFixed size={17} strokeWidth={2} />
              <span className="text-sm">{showUserLoc && geo?.status === "searching" ? "Se caută…" : "Locația mea"}</span>
            </button>
            {isSuperAdmin && (
              <button data-testid="mobile-start-geo-calib-btn" onClick={startGeoCalib}
                className="flex items-center gap-2 px-3 h-9 bg-white text-[#7C3AED] border border-[#7C3AED] shadow text-sm">
                <Satellite size={15} strokeWidth={2} /> Calibrare GPS
              </button>
            )}
            {showUserLoc && geo?.status === "denied" && (
              <div data-testid="mobile-geo-denied-hint" className="max-w-[220px] bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] text-[11px] p-2">
                Locația e blocată. Reactiveaz-o din setările site-ului (🔒), apoi apasă din nou.
              </div>
            )}
            {showUserLoc && geo?.status === "active" && geo?.coords && (
              <div className="text-[11px] text-zinc-700 bg-white/90 px-2 py-1 border border-[#C3DBA0] font-mono-data">
                ±{Math.round(geo.coords.accuracy)} m
              </div>
            )}
          </div>
        )}

        {/* GPS calibration in progress (admin) */}
        {tool === "geocalib" && (
          <div data-testid="mobile-geo-calib-panel" className="absolute bottom-0 left-0 right-0 z-30 bg-white border-t-2 border-[#7C3AED] p-3 space-y-2" style={{ paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom))" }}>
            <div className="flex items-center gap-2 font-head text-sm font-bold text-[#7C3AED]">
              <Satellite size={15} strokeWidth={2} /> Calibrare GPS ({geoCalibDraft.length}/3)
            </div>
            <p className="text-[11px] text-zinc-500">Atinge pe hartă unde te afli, apoi capturează GPS-ul. 3 puncte răspândite.</p>
            {geoCalibDraft.length >= 3 && geoDraftQuality && !geoDraftQuality.degenerate && (
              <div data-testid="mobile-geo-quality" className="text-[11px] font-mono-data text-zinc-600">
                Potrivire: <span className={geoDraftQuality.rating === "good" ? "text-[#166534]" : geoDraftQuality.rating === "ok" ? "text-[#92400E]" : "text-[#991B1B]"}>
                  {geoDraftQuality.rating === "good" ? "Foarte bună" : geoDraftQuality.rating === "ok" ? "Acceptabilă" : "Slabă"}
                </span> · eroare max {geoDraftQuality.maxAbsM.toFixed(1)} m ({(geoDraftQuality.maxRel * 100).toFixed(1)}%)
              </div>
            )}
            <div className="flex gap-2">
              {geoCalibDraft.length >= 3 ? (
                <button data-testid="mobile-geo-calib-save-btn" onClick={saveGeoCalibration}
                  className="flex-1 h-10 bg-[#7C3AED] text-white text-sm font-medium">Salvează calibrarea</button>
              ) : (
                <div className="flex-1 text-[11px] text-zinc-500 flex items-center">Mai adaugă {3 - geoCalibDraft.length} punct(e)…</div>
              )}
              <button data-testid="mobile-geo-calib-cancel-btn" onClick={cancelGeoCalib}
                className="h-10 px-3 border border-[#A8C97D] text-zinc-700 text-sm">Anulează</button>
            </div>
          </div>
        )}

        {/* GPS capture prompt */}
        {geoPendingPixel && (
          <div className="fixed inset-0 z-40 bg-black/50 flex items-end justify-center p-4" data-testid="mobile-geo-capture-overlay" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
            <div className="w-full max-w-md bg-white border-2 border-[#7C3AED] p-4 space-y-3">
              <div className="font-head text-base font-bold text-[#7C3AED] flex items-center gap-2">
                <Satellite size={16} /> Capturează GPS — punct {geoCalibDraft.length + 1}/3
              </div>
              <p className="text-sm text-zinc-600">Stai fix în locul marcat pe hartă și apasă butonul.</p>
              {geo?.status === "denied" && (
                <div className="bg-[#FEF2F2] border border-[#FECACA] text-[#991B1B] text-xs p-2">
                  Accesul la locație e blocat. Reactiveaz-o din setările site-ului, apoi încearcă din nou.
                </div>
              )}
              <button data-testid="mobile-geo-capture-btn" onClick={captureGeoPoint} disabled={geoCapturing}
                className="w-full h-12 bg-[#7C3AED] text-white font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                <LocateFixed size={18} /> {geoCapturing ? "Se citește GPS-ul…" : "Capturează GPS aici"}
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Search overlay */}
      {searchOpen && (
        <div data-testid="mobile-search-overlay" className="fixed inset-0 z-40 bg-black/40 flex items-start justify-center p-4 pt-16"
          onClick={() => setSearchOpen(false)}>
          <div className="w-full max-w-md bg-white border border-[#A8C97D] shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 border-b border-[#C3DBA0] px-3 h-12">
              <Search size={16} className="text-zinc-400 shrink-0" />
              <Input ref={inputRef} data-testid="mobile-search-input" placeholder="Caută element după nume…"
                value={query} onChange={(e) => setQuery(e.target.value)}
                className="border-0 rounded-none focus-visible:ring-0 shadow-none px-0 h-12 text-base" />
              <button data-testid="mobile-search-close-btn" onClick={() => setSearchOpen(false)}
                className="shrink-0 w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-[#E63946]">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <div className="max-h-72 overflow-y-auto no-scrollbar">
              {query.trim() && results.length === 0 && (
                <div className="px-4 py-6 text-sm text-zinc-400 text-center">Niciun element găsit.</div>
              )}
              {results.map((el) => {
                const c = categories.find((cc) => cc.id === el.category_id);
                return (
                  <button key={el.id} data-testid={`mobile-search-result-${el.id}`} onClick={() => pickElement(el)}
                    className="w-full flex items-center justify-between gap-2 px-4 h-12 text-left border-b border-zinc-100 hover:bg-zinc-50">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 shrink-0" style={{ background: c?.color || "#5E8A1E" }} />
                      <span className="truncate text-sm">{el.name}</span>
                    </span>
                    {isTeam && (
                      <span className="font-mono-data text-xs text-zinc-500 shrink-0">{el.consumption} kW</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Bottom sheet with element details */}
      <div
        data-testid="mobile-details-sheet"
        ref={sheetRef}
        className={`fixed left-0 right-0 bottom-0 z-30 bg-white border-t-2 border-[#5E8A1E] shadow-[0_-8px_24px_rgba(0,0,0,0.18)] transition-transform duration-300 ${
          selected && isTeam ? "translate-y-0" : "translate-y-full"
        }`}
        style={{ maxHeight: "60vh" }}
      >
        {selected && isTeam && (
          <div className="overflow-y-auto no-scrollbar p-4 space-y-3" style={{ maxHeight: "60vh" }}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-head text-lg font-bold leading-tight">{selected.name}</div>
                <div className="text-xs text-zinc-500 flex items-center gap-1 mt-0.5">
                  <span className="w-2.5 h-2.5 inline-block" style={{ background: cat?.color || "#5E8A1E" }} />
                  {cat?.name || "—"}
                </div>
              </div>
              <button data-testid="mobile-close-details-btn" onClick={() => onSelect(null)}
                className="shrink-0 w-8 h-8 flex items-center justify-center text-zinc-500 hover:text-[#E63946]">
                <X size={20} strokeWidth={1.5} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="border border-[#C3DBA0] bg-zinc-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">Dimensiune</div>
                <div className="font-mono-data text-sm text-zinc-800">{selected.length}×{selected.width} m</div>
              </div>
              {isTeam && (
              <div className="border border-[#C3DBA0] bg-zinc-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-wide text-zinc-500 flex items-center gap-1">
                  <Zap size={11} className="text-[#FFC300]" /> Consum
                </div>
                <div className="font-mono-data text-sm text-zinc-800">{selected.consumption} kW</div>
              </div>
              )}
            </div>

            {isTeam && [["Mobilier", selected.mobilier], ["Branding", selected.branding], ["Altele", selected.altele]].map(([title, v]) => (
              <div key={title}>
                <div className="text-[10px] uppercase tracking-wide text-zinc-500">{title}</div>
                <div data-testid={`mobile-details-${title.toLowerCase()}`} className="text-sm whitespace-pre-wrap text-zinc-800">{v ? v : "—"}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
